import { useCallback, useRef, useState } from "react";

import { CompetitionCard } from "../../Competitions/Hub/CompetitionCard";
import { FeaturedCompetition } from "./FeaturedCompetition";
import { CompetitionsBannerViewModel } from "./ViewModel";
import {
  SectionWrapper,
  MoreWrap,
  MoreHeading,
  MoreRow,
} from "./styledComponents";

// Must match the grid track sizing in styledComponents' MoreRow.
const MIN_CARD_WIDTH = 300;
const ROW_GAP = 18;

// How many cards fit in exactly one row at the current width, so ended
// competitions fill the row without ever wrapping to a second line. Uses a
// callback ref (not a mount effect) so measuring happens when the row node
// actually attaches — the component renders null while the query is loading,
// so the node doesn't exist on first mount.
const useRowCapacity = () => {
  const [columns, setColumns] = useState(1);
  const observerRef = useRef<ResizeObserver | null>(null);

  const rowRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (!node) return;

    const measure = () => {
      const width = node.clientWidth;
      if (!width) return;
      const next = Math.max(
        1,
        Math.floor((width + ROW_GAP) / (MIN_CARD_WIDTH + ROW_GAP)),
      );
      setColumns(next);
    };

    measure();
    observerRef.current = new ResizeObserver(measure);
    observerRef.current.observe(node);
  }, []);

  return { rowRef, columns };
};

export const CompetitionsBanner = () => {
  const { featured, others, loading } = CompetitionsBannerViewModel();
  const { rowRef, columns } = useRowCapacity();

  if (loading || (!featured && others.length === 0)) return null;

  const visible = others.slice(0, columns);

  return (
    <SectionWrapper>
      {featured && <FeaturedCompetition competition={featured} />}

      {others.length > 0 && (
        <MoreWrap>
          <MoreHeading>Check out more competitions</MoreHeading>
          <MoreRow ref={rowRef}>
            {visible.map((competition) => (
              <CompetitionCard
                key={competition._id}
                competition={competition}
              />
            ))}
          </MoreRow>
        </MoreWrap>
      )}
    </SectionWrapper>
  );
};
